import {ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, model, output, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ICycle, MinimalUserModel, UserGroup} from '@core/api/data-access';
import {CycleModel} from '@cycles/data-access';
import {GlobalSearchCategory, SearchResultItem} from '@global-search/data-access';
import {TranslateModule} from '@ngx-translate/core';
import {IconComponent} from '@shared/icon';
import {TranslateArrayPipe} from '@utils';
import {DropdownModule} from 'primeng/dropdown';
import {TooltipModule} from 'primeng/tooltip';
import {GlobalSearchUiStore} from '../../store/global-search-ui.store';
import {CycleResultItemComponent} from '../result-items/cycle-result-item/cycle-result-item.component';
import {UserResultItemComponent} from '../result-items/user-result-item/user-result-item.component';
import {UserGroupResultItemComponent} from '../result-items/usergroup-result-item/usergroup-result-item.component';

@Component({
	selector: 'dc-search-results', // Standard prefix 'dc'
	imports: [IconComponent, CycleResultItemComponent, UserResultItemComponent, UserGroupResultItemComponent, TranslateModule, DropdownModule, FormsModule, TooltipModule, TranslateArrayPipe],
	templateUrl: './search-results.component.html',
	styleUrl: './search-results.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultsComponent {
	// --- Inputs ---
	readonly results = input.required<SearchResultItem[]>(); // Array of results (use union type)
	readonly category = input.required<GlobalSearchCategory | null>(); // Current category
	readonly loading = input<boolean>(false); // Loading state
	readonly error = input<unknown>(null); // Error state
	readonly total = input<number>(0); // Total number of results available
	readonly searchTerm = input<string | null>(null); // Current search term for "no results" message
	readonly sortBy = input.required<string>(); // Current sort field
	readonly sortDirection = input<'asc' | 'desc'>('asc'); // Current sort direction
	// --- Outputs ---
	readonly loadMore = output<void>(); // Event emitted when "Load More" is clicked
	readonly pageSize = model.required<number>(); // Two-way binding for page size
	readonly sortByChange = output<string>(); // Event emitted when sort field changes
	readonly sortDirectionChange = output<'asc' | 'desc'>(); // Event emitted when sort direction changes
	protected readonly elementRef: ElementRef = inject(ElementRef);
	protected readonly uiStore = inject(GlobalSearchUiStore); // Inject the UI store for state management
	// --- Expose Enum to Template ---
	protected readonly GlobalSearchCategory = GlobalSearchCategory;

	// --- Computed Signals ---
	/** Determines if the 'Load More' button should be shown */
	protected readonly showLoadMoreButton = computed(() => {
		const currentResults = this.results();
		// Show if not loading, there are some results, and the current count is less than total
		return !this.loading() && currentResults.length > 0 && currentResults.length < this.total();
	});

	/** Determines if the "no results found" message should be shown */
	protected readonly showNoResultsMessage = computed(() => {
		// Show if not loading, no error, search term exists, and results array is empty
		return !this.loading() && !this.error() && !!this.searchTerm() && this.results().length === 0;
	});

	/** Available sort options based on the current category */
	protected readonly sortOptions = computed(() => {
		const activeCategoryConfigs = this.uiStore.activeCategoryConfigs$();
		if (!activeCategoryConfigs) return [];
		return activeCategoryConfigs.sortOptions.map(option => ({
			...option,
			label: option.label || option.value,
			value: option.value,
		}));
	});

	// Signal to track if results were recently loaded
	protected isNewlyLoaded = signal<boolean>(false);

	/** ----------------------- Constructor ----------------- */
	constructor() {
		// Set up effect to scroll to the last item when new results are loaded
		effect(() => {
			const isLoading = this.loading();

			if (!isLoading && this.isNewlyLoaded()) {
				setTimeout(() => {
					const lastItem = this.elementRef.nativeElement.querySelector('#last-result-item');
					if (lastItem) {
						lastItem.scrollIntoView({behavior: 'smooth', block: 'center'});
					}
					this.isNewlyLoaded.set(false);
				}, 100);
			}
		});
	}

	// --- Methods ---

	/** Emits the loadMore event when the button is clicked */
	protected onLoadMoreClick(): void {
		this.isNewlyLoaded.set(true);
		this.loadMore.emit();
	}

	/**
	 * Handles page size change events from the dropdown
	 * @param size
	 */
	protected onPageSizeChange(size: number): void {
		size = Number(size);
		this.pageSize.set(size);
	}

	/**
	 * Handles sort field change from the dropdown
	 * @param field The new sort field
	 */
	protected onSortByChange(field: string): void {
		this.sortByChange.emit(field);
	}

	/**
	 * Toggles or sets the sort direction
	 * @param direction Optional direction to set explicitly, or toggle if not provided
	 */
	protected onSortDirectionChange(direction?: 'asc' | 'desc'): void {
		const newDirection = direction || (this.sortDirection() === 'asc' ? 'desc' : 'asc');
		this.sortDirectionChange.emit(newDirection as 'asc' | 'desc');
	}

	/**
	 * TrackBy function for the results loop.
	 * Uses a combination of category and item ID for uniqueness.
	 * Assumes each result item type has a unique 'id' property.
	 * @param index The index of the item.
	 * @param item The result item object.
	 * @returns A unique identifier string.
	 */
	protected trackByResultItem(index: number, item: SearchResultItem): string | number {
		// Make sure all your result types (ICycle, MinimalUserModel, UserGroup) have a unique 'id' property
		return `${this.category()}-${item.id}`;
	}

	// Type guard functions to help the template compiler with @switch
	// (Not strictly necessary with `as` casting but improves type safety)
	protected isCycle(item: SearchResultItem): item is ICycle {
		return this.category() === GlobalSearchCategory.CYCLE;
	}

	protected isUser(item: SearchResultItem): item is MinimalUserModel {
		return this.category() === GlobalSearchCategory.USER;
	}

	protected isUserGroup(item: SearchResultItem): item is UserGroup {
		return this.category() === GlobalSearchCategory.USER_GROUP;
	}

	// Helper to cast item type within the template (alternative to type guards)
	protected castToCycle(item: SearchResultItem): ICycle {
		return item instanceof CycleModel ? item : new CycleModel(item as ICycle);
	}

	protected castToUser(item: SearchResultItem): MinimalUserModel {
		return item as MinimalUserModel;
	}

	protected castToUserGroup(item: SearchResultItem): UserGroup {
		return item as UserGroup;
	}
}
